import { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

// Link a debit card → load the wallet straight off it. The Stripe rail (setup → attach → load →
// confirm) is server-side and already live; this is its face. Card numbers live in Stripe's
// element — this page never sees them. For Dior: her PayPal debit card loads her wallet with no
// bank account and no ATM in the loop.

const CardAttachForm = ({ clientSecret, onDone, onError, busy, setBusy }) => {
  const stripe = useStripe();
  const elements = useElements();

  const attach = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    try {
      const card = elements.getElement(CardElement);
      const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card },
      });
      if (error) throw new Error(error.message);
      await onDone(setupIntent.payment_method);
    } catch (e) {
      onError(e.message || "The card could not be linked.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="card-link-form" className="space-y-3">
      <div data-testid="card-element-wrap" className="rounded-lg border border-stone-700 bg-stone-950/70 px-3 py-3">
        <CardElement options={{ style: { base: { color: "#e7e5e4", fontSize: "15px", "::placeholder": { color: "#78716c" } } } }} />
      </div>
      <button onClick={attach} disabled={busy || !stripe} data-testid="card-link-button"
        className="w-full rounded-lg bg-amber-500 py-2.5 text-xs font-bold uppercase tracking-widest text-stone-950 disabled:opacity-40">
        {busy ? "Linking…" : "Link this card"}
      </button>
    </div>
  );
};

export const WalletCardLoad = ({ api, token, onLoaded }) => {
  const [cards, setCards] = useState([]);
  const [mode, setMode] = useState("test");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [linking, setLinking] = useState(null); // { clientSecret, stripePromise }
  const [amounts, setAmounts] = useState({});

  const auth = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const loadCards = async () => {
    try {
      const res = await fetch(`${api}/wallet/cards`, { headers: auth });
      if (!res.ok) { setMode("off"); return; }
      const d = await res.json();
      setCards(d.cards || []);
      setMode(d.mode || "test");
    } catch { setMode("off"); }
  };
  useEffect(() => { loadCards(); }, [token]);

  // the panel only exists when a live acquirer backs it — no live Stripe account (EIN underwriting
  // the platform doesn't run), so until a live rail lands this stays off every wallet quietly
  if (mode !== "live") return null;

  const startLink = async () => {
    setBusy(true); setMsg("");
    try {
      const res = await fetch(`${api}/wallet/cards/setup`, { method: "POST", headers: auth, body: "{}" });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Card setup failed");
      setLinking({ clientSecret: d.client_secret, stripePromise: loadStripe(d.publishable_key) });
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  const finishAttach = async (paymentMethodId) => {
    const res = await fetch(`${api}/wallet/cards/attach`, {
      method: "POST", headers: auth, body: JSON.stringify({ payment_method_id: paymentMethodId }),
    });
    const d = await res.json();
    if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Attach failed");
    setLinking(null);
    setMsg(`Linked ${d.card.brand} •••• ${d.card.last4} — load straight from it below.`);
    await loadCards();
  };

  const load = async (pmId) => {
    const amount = parseFloat(amounts[pmId] || "0");
    if (!amount || amount <= 0) { setMsg("Enter an amount first."); return; }
    setBusy(true); setMsg("");
    try {
      const res = await fetch(`${api}/wallet/cards/load`, {
        method: "POST", headers: auth, body: JSON.stringify({ payment_method_id: pmId, amount_usd: amount }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Load failed");
      // confirm the charge in the browser — the member is present for any bank challenge
      const keyRes = await fetch(`${api}/wallet/cards/setup`, { method: "POST", headers: auth, body: "{}" });
      const keyData = await keyRes.json();
      const stripe = await loadStripe(keyData.publishable_key);
      const { error } = await stripe.confirmCardPayment(d.client_secret);
      if (error) throw new Error(error.message);
      const conf = await fetch(`${api}/wallet/cards/load/confirm`, {
        method: "POST", headers: auth, body: JSON.stringify({ payment_intent_id: d.payment_intent_id }),
      });
      const cd = await conf.json();
      if (!conf.ok) throw new Error(typeof cd.detail === "string" ? cd.detail : "Confirm failed");
      setMsg(`Loaded ${fmt(amount)} from ${d.card_label} — the wallet holds it now.`);
      setAmounts((a) => ({ ...a, [pmId]: "" }));
      onLoaded?.();
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  const remove = async (pmId) => {
    setBusy(true);
    try {
      await fetch(`${api}/wallet/cards/${pmId}`, { method: "DELETE", headers: auth });
      await loadCards();
    } finally { setBusy(false); }
  };

  const fmt = (n) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

  return (
    <div data-testid="card-load-panel" className="rounded-xl border border-stone-700 bg-stone-900/60 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-mono uppercase tracking-widest text-amber-500/80">Load by debit card</p>
        {mode !== "live" && (
          <span className="text-[9px] font-mono uppercase tracking-widest text-amber-400/70 border border-amber-500/30 rounded-full px-2 py-0.5" data-testid="card-mode-badge">test mode</span>
        )}
      </div>
      <p className="text-[9px] font-mono text-stone-500">link a card once · loads land straight in the wallet — no bank, no ATM</p>

      {cards.map((c) => (
        <div key={c.payment_method_id} data-testid={`card-row-${c.last4}`}
          className="flex items-center gap-2 rounded-lg border border-stone-700/60 p-2.5">
          <p className="text-sm font-bold text-stone-100 flex-1">{c.brand} •••• {c.last4}</p>
          <input
            type="number" min="1" step="0.01" placeholder="0.00"
            value={amounts[c.payment_method_id] || ""}
            onChange={(e) => setAmounts((a) => ({ ...a, [c.payment_method_id]: e.target.value }))}
            data-testid={`card-load-amount-${c.last4}`}
            className="w-24 rounded-lg border border-stone-700 bg-stone-950/70 px-3 py-2 text-sm text-stone-100" />
          <button onClick={() => load(c.payment_method_id)} disabled={busy}
            data-testid={`card-load-button-${c.last4}`}
            className="rounded-lg bg-emerald-500 px-4 py-2 text-xs font-bold uppercase tracking-widest text-stone-950 disabled:opacity-40">
            Load
          </button>
          <button onClick={() => remove(c.payment_method_id)} disabled={busy}
            data-testid={`card-remove-${c.last4}`}
            className="text-[10px] font-mono text-stone-500 hover:text-red-400">remove</button>
        </div>
      ))}

      {linking ? (
        <Elements stripe={linking.stripePromise}>
          <CardAttachForm clientSecret={linking.clientSecret} onDone={finishAttach}
            onError={setMsg} busy={busy} setBusy={setBusy} />
        </Elements>
      ) : (
        <button onClick={startLink} disabled={busy} data-testid="card-add-button"
          className="w-full rounded-lg border border-stone-600 py-2.5 text-xs font-bold uppercase tracking-widest text-stone-200 hover:border-amber-500/60 transition-colors disabled:opacity-40">
          {cards.length ? "Link another card" : "Link a debit card"}
        </button>
      )}

      {msg && <p className="text-[11px] font-mono text-amber-300/90" data-testid="card-load-msg">{msg}</p>}
      <p className="text-[9px] text-stone-600 leading-relaxed">
        Card details are handled by Stripe and never touch this site. Your bank may ask you to confirm a load.
      </p>
    </div>
  );
};
